import React, { useMemo, useState } from "react";
import { Question, TrendEvent } from "../../endpoints/results.type";
import { TimeSeriesDataPoint } from "src/redux/admin/reporting/reporting.types";
import type { ChartEventMarker } from "src/pages/reports/components/metric-chart";
import MetricStackedBarChart from "src/pages/reports/components/metric-stacked-bar-chart/metric-stacked-bar-chart";
import { BarChart, LineChart } from "lucide-react";
import MetricChart from "src/pages/reports/components/metric-chart";
import QuestionAnswerButton from "./question-answer-button";
import TrendEventDetail from "./trend-event-detail";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "src/components/ui/dialog";

interface QuestionCardProps {
  index: number;
  question: Question;
  selectedDate: string;
  selectedInterval: number;
  selectedSurveyAnswers: string[];
  isLoading: boolean;
  onAnswerClick: (answerId: string) => void;
  trendEvents?: TrendEvent[];
}

const colors = ['bg-yellow-500', 'bg-blue-500', 'bg-green-501', 'bg-red-502', 'bg-purple-500', 'bg-orange-500', 'bg-black', 'bg-gray-600', 'bg-green-1000' ];
const chartColors = ['#F9AB00', '#3257FB', '#1e8e3c', '#d93026', '#934DFF', '#F25A18', 'black', 'gray', 'green' ];

const QuestionCard: React.FC<QuestionCardProps> = ({
  index,
  question,
  selectedDate,
  selectedInterval,
  selectedSurveyAnswers,
  isLoading,
  onAnswerClick,
  trendEvents = [],
}) => {
  const [chartType, setChartType] = useState<'bar' | 'line'>('line');
  const [selectedEvent, setSelectedEvent] = useState<TrendEvent | null>(null);

  const eventDateOnlySet = useMemo(() => {
    return new Set(trendEvents.map((e) => e.event_date.split("T")[0]));
  }, [trendEvents]);

  // Get all unique dates from all answer intervals
  const allDates = new Set<string>();
  Object.values(question.answers).forEach(answer => {
    answer.intervals.forEach(interval => {
      allDates.add(interval.interval_start.split("T")[0]);
    });
  });
  // Ensure the chart's X-domain includes every trend event date,
  // even if that date doesn't appear in this specific question's intervals.
  eventDateOnlySet.forEach((d) => allDates.add(d));

  const answerIds = Object.values(question.answers).map((answer) => answer.id);

  // Create data points grouped by date, with each answer's raw_percent as a property
  const chartDataSorted = Array.from(allDates)
    .map((date) => {
      const dataPoint: any = { date };

      // For each answer, find the interval for this date and add its raw_percent (converted to 0-100 scale)
      Object.values(question.answers).forEach((answer) => {
        const interval = answer.intervals.find(
          (interval) => interval.interval_start.split("T")[0] === date
        );
        if (interval) {
          dataPoint[answer.id] = interval.raw_percent * 100;
        }
      });

      return dataPoint as TimeSeriesDataPoint;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Forward/back-fill so event-only dates don't create gaps in the line charts.
  answerIds.forEach((answerId) => {
    // Forward fill.
    let lastValue: number | undefined = undefined;
    chartDataSorted.forEach((dp) => {
      const value = dp[answerId];
      const isNumber = typeof value === "number" && !Number.isNaN(value);
      if (isNumber) {
        lastValue = value as number;
        return;
      }
      if (lastValue !== undefined) {
        dp[answerId] = lastValue;
      }
    });

    // Backfill (for leading missing values).
    let nextValue: number | undefined = undefined;
    [...chartDataSorted].reverse().forEach((dp) => {
      const value = dp[answerId];
      const isNumber = typeof value === "number" && !Number.isNaN(value);
      if (isNumber) {
        nextValue = value as number;
        return;
      }
      if (nextValue !== undefined) {
        dp[answerId] = nextValue;
      }
    });

    // Final fallback to 0 (keeps charts stable even if an answer never appears).
    chartDataSorted.forEach((dp) => {
      const value = dp[answerId];
      const isNumber = typeof value === "number" && !Number.isNaN(value);
      if (!isNumber) {
        dp[answerId] = 0;
      }
    });
  });

  // Filter out data points where all raw_percent values are zero, except dates with a trend event.
  const chartData = chartDataSorted.filter((dataPoint) => {
    const hasNonZeroValue = answerIds.some((answerId) => {
      const value = dataPoint[answerId];
      return value !== undefined && value !== null && value !== 0;
    });

    // Keep dates where a trend event exists so the chart X-domain includes the event marker.
    return hasNonZeroValue || eventDateOnlySet.has(dataPoint.date);
  });

  const eventMarkers: ChartEventMarker[] = useMemo(() => {
    if (!trendEvents.length) return [];
    return trendEvents.map((e) => ({
      id: e.id,
      event_date: e.event_date,
      short_title: e.short_title,
    }));
  }, [trendEvents]);

  // Create metrics array with answer IDs as keys
  const metrics = Object.values(question.answers).map((answer, index) => ({
    key: answer.id,
    name: answer.title,
    color: chartColors[index % chartColors.length],
  }));

  return (
    <div className="rounded-lg shadow justify-between bg-white mb-4 overflow-hidden">
      <div className="flex flex-row items-stretch justify-between">
        <div className="flex flex-row">
          {Object.values(question.answers).map((answer, index) => (
            <QuestionAnswerButton
              key={answer.id}
              answer={answer}
              label={answer.title}
              color={colors[index % chartColors.length]}
              selected={selectedSurveyAnswers.length == 0 ? true : selectedSurveyAnswers.includes(answer.id) ? true : false}
              onClick={() => onAnswerClick(answer.id)}
            />
          ))}
        </div>
        <div className="flex items-stretch">
          <button
            type="button"
            className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-65 disabled:opacity-50 px-4 border-b border-l border-gray-201 ${chartType === 'bar' ? 'bg-gray-201' : 'bg-white'}`}
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-6 h-6 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
            <span className="text-xs font-light">
              Bar
            </span>
          </button>
          <button
            type="button"
            className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-65 disabled:opacity-50 px-4 border-l border-b border-gray-201 ${chartType === 'line' ? 'bg-gray-201' : 'bg-white'}`}
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-6 h-6 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
            <span className="text-xs font-light">
              Line
            </span>
          </button>
          {/* <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-4 h-4 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-4 h-4 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
          </Button> */}
        </div>
      </div>

      <div className="flex flex-row justify-between items-center p-4 pb-0">
        {/* <div className="flex items-center">
          <div className="flex size-6 shrink-0 rounded-full bg-gray-500 items-center justify-center">
            <div className="text-white">
              {index + 1}
            </div>
          </div>
          <h4 className="text-2xl font-light text-gray-600 ml-4">
            {question.title}
          </h4>
        </div> */}
        {/* <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-4 h-4 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-4 h-4 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
        </div> */}

      </div>
      {chartType === 'bar' ? (
        <MetricStackedBarChart
          className='border-none !p-2 !pb-4'
          data={chartData}
          metrics={metrics}
          isLoading={false}
          isNormalized={false}
          sharedYAxisDomain={true}
          eventMarkers={eventMarkers}
          onEventMarkerClick={(id) => setSelectedEvent(trendEvents.find((e) => e.id === id) ?? null)}
          showBrush={eventMarkers.length > 0}
        />
      ) : (
        <MetricChart
          className='border-none !p-2 !pb-4'
          data={chartData}
          metrics={metrics}
          isLoading={false}
          isNormalized={false}
          sharedYAxisDomain={true}
          eventMarkers={eventMarkers}
          onEventMarkerClick={(id) => setSelectedEvent(trendEvents.find((e) => e.id === id) ?? null)}
          showBrush={eventMarkers.length > 0}
        />
      )}
      <Dialog
        open={!!selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      >
        <DialogContent className="max-w-md bg-white">
          <DialogTitle className="sr-only">
            Event details
          </DialogTitle>
          {selectedEvent && (
            <TrendEventDetail event={selectedEvent} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionCard;